<template>
  <main>
    <div class="container">
      <al-notify v-if="showBanner" success name="promotional-banner">
        {{ $t(bannerText) }}
      </al-notify>
      <div class="row">
        <div class="col-full">
          <h1 class="heading-xl">
            {{ $t(content.heading) }}
          </h1>
          <p>
            {{
              $t(content.subheading, {
                issuer: content.issuerNames[0],
                issuer1: content.issuerNames[0],
                issuer2: content.issuerNames[1]
              })
            }}
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <al-list card>
            <al-list-item
              v-for="card in unprovisionedCards"
              :key="card.id"
              :name="`card-info-${card.id}`"
              class="clickable"
              cta-icon="add"
              @click="handleCardClick(card)">
              <al-card-preview
                :img="card.artUrl"
                :brand="card.shortProductDescription || card.issuerName || card.paymentNetworkName"
                :number="`•••• ${card.primaryAccountNumberSuffix}`"
                :name="`card-list-preview`" />
            </al-list-item>
          </al-list>
        </div>
      </div>

      <div class="row row-vertical-align-bottom">
        <div class="col-full align-center">
          <back-button
            v-analytics:click="$EVENTS.CANCEL_BUTTON"
            :location="backButtonRoute"
            @click="handleBackButtonClick">
            {{ $t('button.notNow') }}
          </back-button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { AlCardPreview, AlList, AlListItem, AlNotify } from '@mc-alberta/alcapone'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import BackButton from '@/components/BackButton'
import { ACTION, BULK, ROUTES } from '@/constants'
import * as types from '@/store/mutation-types.js'

export default {
  name: ROUTES.LIST_CARDS_UNPROVISIONED,
  components: { AlCardPreview, AlList, AlListItem, AlNotify, BackButton },
  data() {
    let backButtonRoute

    if (this.$store.state.cards.cards.length) {
      backButtonRoute = { name: ROUTES.PROFILE_HOME }
    } else {
      backButtonRoute = { name: ROUTES.ADD_CARD }
    }

    return {
      backButtonRoute,
      isNewUser: this.$route.params.isNewUser,
      showBanner: this.$route.params.showBanner,
      bulkRetriesRemaining: BULK.RETRY_LIMIT,
      selectedCard: ''
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'serviceErrorResponse']),
    ...mapState({
      unprovisionedCards: (state) => state.cards.unprovisionedCards
    }),
    bannerText() {
      return this.isNewUser
        ? 'unprovisionedCards.bannerCreatedProfile'
        : 'unprovisionedCards.bannerAddedCard'
    },
    content() {
      const uniqueIssuerNames = new Set() // to hold unique issuer names
      this.unprovisionedCards.forEach((card) => uniqueIssuerNames.add(card.issuerName))
      const issuerNames = [...uniqueIssuerNames]

      let heading = 'unprovisionedCards.headings.multipleBanks'
      let subheading = 'unprovisionedCards.subheadings.multipleBanks'

      if (this.unprovisionedCards.length === 1) {
        // In case of a single unprovisioned card from a single bank
        heading = 'unprovisionedCards.headings.singleCardSingleBank'
        subheading = 'unprovisionedCards.subheadings.singleCardSingleBank'
      } else if (this.unprovisionedCards.length > 1 && uniqueIssuerNames.size === 1) {
        // In case of a multiple unprovisioned cards from a single bank
        heading = 'unprovisionedCards.headings.multipleCardsSingleBank'
        subheading = 'unprovisionedCards.subheadings.multipleCardsSingleBank'
      } else if (uniqueIssuerNames.size === 2) {
        // In case of a multiple cards from two banks
        heading = 'unprovisionedCards.headings.multipleBanks'
        subheading = 'unprovisionedCards.subheadings.multipleBanks'
      }

      return { heading, subheading, issuerNames }
    },
    isExistingUser() {
      return this.isLoggedIn
    }
  },
  mounted() {
    if (!this.unprovisionedCards.length) {
      return this.$router.replace({ name: ROUTES.ENTRY })
    }

    if (!this.isLoggedIn) {
      this.setAction(ACTION.REGISTER)
    }
  },
  beforeDestroy() {
    this.clearServiceLayerError()
  },
  methods: {
    ...mapActions(['clearServiceLayerError', 'fetchUnprovisionedProfile', 'setAction']),
    ...mapMutations({
      setSkipUnprovision: types.SET_SKIP_UNPROVISION,
      setInterceptUnauthorizedUser: types.SET_INTERCEPT_UNAUTHORIZED_ERROR
    }),
    handleBackButtonClick() {
      this.setSkipUnprovision(true)
    },
    async handleCardClick(card) {
      this.$analytics.track({
        event: this.$EVENTS.ADD_CARD_BUTTON,
        meta: {
          digitalCardStatus: card.status
        }
      })
      this.selectedCard = card
      this.handleUnprovisionedProfileCall()
    },
    async handleUnprovisionedProfileCall() {
      this.clearServiceLayerError()
      if (!this.isExistingUser) {
        this.setInterceptUnauthorizedUser(false)
      }
      await this.fetchUnprovisionedProfile(this.selectedCard.id)
      this.setInterceptUnauthorizedUser(true)

      if (!this.serviceErrorResponse()) {
        this.$router.replace({ name: ROUTES.REVIEW_PROVISION })
      } else if (this.bulkRetriesRemaining > 1) {
        this.retryBulk()
      } else {
        this.errorRouting()
      }
    },
    errorRouting() {
      if (this.isExistingUser) {
        this.setSkipUnprovision(true)
        this.$router.replace({
          name: ROUTES.LIST_CARDS,
          params: { errorToken: 'cards.unprovisionRetryError' }
        })
      } else {
        this.$router.replace({
          name: ROUTES.ADD_CARD,
          params: { errorToken: 'cards.unprovisionRetryError' }
        })
      }
    },
    retryBulk() {
      this.bulkRetriesRemaining--

      this.handleUnprovisionedProfileCall()
    }
  }
}
</script>
